import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';

/**
 * Lightweight MUI table driven by a column-descriptor list.
 *
 * Column shape: `{ title, dataIndex, render?, width?, align?, key? }`
 *   - `dataIndex` picks the value off the row object
 *   - `render(value, row, index)` returns custom cell content (optional)
 *
 * Keeps the declarative `columns`/`data` contract the dashboard widgets are
 * written against, with rendering that is pure MUI and theme-aware.
 */
export default function DataTable({
  columns = [], data = [], rowKey, size = 'small', stickyHeader = true, sx,
}) {
  const keyFor = (row, i) => {
    if (typeof rowKey === 'function') return rowKey(row, i);
    if (rowKey && row?.[rowKey] != null) return row[rowKey];
    if (row?.key != null) return row.key;
    return i;
  };

  const cellKey = (col, ci) => col.key || col.dataIndex || ci;

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '100%', ...sx }}>
      <Table size={size} stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((col, ci) => (
              <TableCell
                key={cellKey(col, ci)}
                align={col.align}
                sx={{ width: col.width, fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {col.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, ri) => (
            <TableRow key={keyFor(row, ri)} hover>
              {columns.map((col, ci) => {
                const value = col.dataIndex != null ? row?.[col.dataIndex] : undefined;
                return (
                  <TableCell key={cellKey(col, ci)} align={col.align} sx={{ width: col.width }}>
                    {col.render ? col.render(value, row, ri) : value}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
